/**
 * /invitation/accept?token=... — unauthenticated invitation acceptance.
 *
 * Reads the raw token from the URL, looks up the invitation metadata to render
 * "join <Org> as <Role>", then lets the invitee set their own password. On
 * success the server logs them in (access token + refresh cookie); we hard-
 * redirect to `/` so the app re-hydrates the session via silentRefresh and the
 * router routes by role.
 *
 * Every token-failure branch (missing / expired / consumed / superseded / not
 * found) maps to the same generic invalid-link UI — no leak about lifecycle.
 */
import { Link, createRoute, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  evaluatePasswordRules,
  validatePasswordStrength,
  type PasswordStrengthReason,
} from '@kora/types';
import { rootRoute } from './root.js';
import { acceptInvitation, lookupInvitation } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

const SearchSchema = z.object({
  token: z.string().optional(),
});

export const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitation/accept',
  validateSearch: (search) => SearchSchema.parse(search),
  component: InvitationAcceptPage,
});

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  hr: 'HR',
  finance: 'Finance',
  employee: 'Employee',
};

interface FormValues {
  password: string;
  confirmPassword: string;
}

const FormSchema = z
  .object({
    password: z.string().min(1, 'Required'),
    confirmPassword: z.string().min(1, 'Required'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match.',
  });

function InvalidLink(): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="w-full max-w-sm space-y-6 p-8 text-center">
        <h1 className="font-serif text-3xl">LumoHR</h1>
        <Alert variant="error">
          This invitation is invalid or no longer valid. Please contact the
          person who invited you.
        </Alert>
        <Link
          to="/login"
          className="inline-block rounded bg-white text-black font-medium py-2 px-4"
        >
          Go to sign in
        </Link>
      </div>
    </main>
  );
}

function InvitationAcceptPage(): JSX.Element {
  const search = useSearch({ from: invitationAcceptRoute.id });
  const token = (search.token ?? '').trim();
  const [serverError, setServerError] = useState<{
    field: 'password' | 'form';
    message: string;
  } | null>(null);
  const [done, setDone] = useState(false);

  const lookup = useQuery({
    queryKey: ['invitation', 'lookup', token],
    queryFn: () => lookupInvitation(token),
    enabled: token.length > 0,
    retry: false,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const rules = evaluatePasswordRules(password);
  const allRulesPass = rules.every((r) => r.satisfied);
  const matches = password.length > 0 && password === confirmPassword;

  const mutation = useMutation({
    mutationFn: acceptInvitation,
    onSuccess: () => {
      setDone(true);
      // Hard redirect so AuthProvider re-hydrates from the fresh refresh
      // cookie via silentRefresh and the router routes by role.
      setTimeout(() => {
        window.location.assign('/');
      }, 1400);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const body = err.body as {
          error?: string;
          reason?: PasswordStrengthReason;
        } | null;
        if (err.status === 400 && body?.error === 'weak_password') {
          setServerError({ field: 'password', message: weakPasswordLabel(body.reason) });
          return;
        }
        if (err.status === 400 && body?.error === 'invalid_token') {
          setServerError({
            field: 'form',
            message: 'This invitation is no longer valid. Contact the person who invited you.',
          });
          return;
        }
        if (err.status === 429) {
          setServerError({
            field: 'form',
            message: 'Too many attempts. Wait a few minutes and try again.',
          });
          return;
        }
      }
      setServerError({ field: 'form', message: 'Could not accept the invitation. Try again.' });
    },
  });

  async function onSubmit(values: FormValues): Promise<void> {
    setServerError(null);
    const strength = validatePasswordStrength(values.password);
    if (!strength.ok) {
      setServerError({ field: 'password', message: weakPasswordLabel(strength.reason) });
      return;
    }
    mutation.mutate({ token, password: values.password });
  }

  // No token, or lookup failed → generic invalid-link UI (no leak).
  if (!token || lookup.isError) {
    return <InvalidLink />;
  }

  if (lookup.isLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="w-full max-w-sm p-8 text-center text-sm text-gray-400">
          Loading invitation…
        </div>
      </main>
    );
  }

  if (done) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="w-full max-w-sm space-y-4 p-8 text-center">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <div
            role="status"
            className="rounded border border-green-900 bg-green-950/40 p-4 text-sm text-green-300"
          >
            Account activated. Signing you in…
          </div>
        </div>
      </main>
    );
  }

  const roleLabel = lookup.data ? (ROLE_LABELS[lookup.data.role] ?? lookup.data.role) : '';
  const canSubmit = allRulesPass && matches && !isSubmitting;

  return (
    <main className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="w-full max-w-sm space-y-6 p-8">
        <header className="text-center space-y-2">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          {lookup.data ? (
            <p className="text-sm text-gray-400">
              You've been invited to join{' '}
              <span className="text-white">{lookup.data.organizationLegalName}</span> as{' '}
              <span className="text-white">{roleLabel}</span>.
            </p>
          ) : null}
        </header>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field
            id="password"
            label="Choose a password"
            type="password"
            autoComplete="new-password"
            error={
              errors.password?.message ??
              (serverError?.field === 'password' ? serverError.message : undefined)
            }
            inputProps={register('password')}
          />
          <ul className="text-xs space-y-1">
            {rules.map((r) => (
              <li key={r.rule} className={r.satisfied ? 'text-green-400' : 'text-gray-500'}>
                {r.satisfied ? '✓' : '○'} {r.label}
              </li>
            ))}
            <li
              className={
                matches
                  ? 'text-green-400'
                  : confirmPassword.length > 0
                    ? 'text-red-400'
                    : 'text-gray-500'
              }
            >
              {matches ? '✓' : '○'} Confirmation matches password
            </li>
          </ul>
          <Field
            id="confirmPassword"
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            inputProps={register('confirmPassword')}
          />

          {serverError?.field === 'form' ? (
            <Alert variant="error">{serverError.message}</Alert>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded bg-white text-black font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Activating…' : 'Accept and set password'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400">
          <Link to="/login" className="underline hover:text-white">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

function weakPasswordLabel(reason: PasswordStrengthReason | undefined): string {
  switch (reason) {
    case 'too_short':
      return 'Password must be at least 12 characters long.';
    case 'no_letter':
      return 'Password must contain at least one letter.';
    case 'no_number':
      return 'Password must contain at least one number.';
    default:
      return 'Password does not meet the strength requirements.';
  }
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  autoComplete?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<FormValues>>['register']>;
}

function Field({ id, label, type = 'text', autoComplete, error, inputProps }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
